<script setup lang="ts">
import CloseIcon from '@rendora/design/assets/circle-error.svg?component';
import EyeOpen from '@rendora/design/assets/eyes.svg?component';
import EyeClose from '@rendora/design/assets/non-eyes.svg?component';
import { computed, ref, watch } from 'vue';
import Input from './Input.vue';

const props = defineProps<{
  modelValue?: string | number;
  placeholder?: string;
  type?: string;
  disabled?: boolean;
  height?: number;
  error?: boolean;
  allowClear?: boolean;
  showPasswordToggle?: boolean;
  showPassword?: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: string | number];
  'update:showPassword': [value: boolean];
}>();

const inputRef = ref<InstanceType<typeof Input>>();
const isHover = ref(false);
const isFocused = ref(false);
const isClearHover = ref(false);
const internalShowPassword = ref(props.showPassword ?? false);

const actualType = computed(() => {
  if (props.type === 'password') {
    return internalShowPassword.value ? 'text' : 'password';
  }
  return props.type ?? 'text';
});

const showClearButton = computed(() => {
  return (
    props.allowClear &&
    props.modelValue &&
    !props.disabled &&
    (isHover.value || isFocused.value || props.error)
  );
});

watch(
  () => props.showPassword,
  val => {
    if (val !== undefined) {
      internalShowPassword.value = val;
    }
  },
);

const handleFocus = () => {
  isFocused.value = true;
};

const handleBlur = () => {
  isFocused.value = false;
};

const handleClear = () => {
  emit('update:modelValue', '');
  inputRef.value?.focus();
};

const togglePassword = () => {
  internalShowPassword.value = !internalShowPassword.value;
  emit('update:showPassword', internalShowPassword.value);
};

const focus = () => inputRef.value?.focus();
const blur = () => inputRef.value?.blur();

// 计算移动端响应式高度
const mobileHeight = computed(() => {
  if (!props.height) return undefined;
  // 移动端响应式计算：基于 375px 设计稿
  // 使用 vw 单位替代 calc() 以提高 Android 兼容性
  // 计算公式：height / 375 * 100vw
  const vwValue = ((props.height / 375) * 100).toFixed(6);
  return `${vwValue}vw`;
});

defineExpose({ focus, blur });
</script>

<template>
  <div
    class="x-input-field"
    :class="{
      'x-input-field--error': error,
      'x-input-field--disabled': disabled,
      'x-input-field--hover': isHover && !disabled && !error,
      'x-input-field--focused': isFocused && !disabled && !error,
    }"
    :style="{
      '--input-height': height ? `${height}px` : undefined,
      '--input-height-mobile': mobileHeight,
    }"
    @mouseenter="isHover = true"
    @mouseleave="isHover = false"
  >
    <Input
      ref="inputRef"
      :model-value="modelValue"
      :type="actualType"
      :placeholder="placeholder"
      :disabled="disabled"
      :aria-invalid="error"
      class="x-input-field__inner"
      @update:model-value="emit('update:modelValue', $event)"
      @focus="handleFocus"
      @blur="handleBlur"
    />
    <div v-if="showClearButton || showPasswordToggle" class="x-input-field__suffix">
      <button
        v-if="showClearButton"
        class="x-input-field__clear"
        :class="{ 'x-input-field__clear--hover': isClearHover }"
        @mouseenter="isClearHover = true"
        @mouseleave="isClearHover = false"
        @click.stop="handleClear"
        type="button"
      >
        <CloseIcon class="x-input-field__clear-icon" />
      </button>
      <button
        v-if="showPasswordToggle"
        class="x-input-field__password-toggle"
        :class="{ 'x-input-field__password-toggle--error': error }"
        @click.stop="togglePassword"
        type="button"
      >
        <EyeOpen v-if="internalShowPassword" class="x-input-field__password-icon" />
        <EyeClose v-else class="x-input-field__password-icon" />
      </button>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '../../styles/mixins.scss' as *;

// 基础容器样式
.x-input-field {
  position: relative;
  width: 100%;
  border: 1px solid var(--x-back-5);
  border-radius: var(--x-radius-medium);
  background: var(--x-back-1);
  display: flex;
  align-items: center;
  padding: 0 16px;
  transition: all 0.2s;
  box-sizing: border-box;
  cursor: text;

  &[style*='--input-height'] {
    height: var(--input-height);
  }

  @include mobile {
    padding: 0 responsive-size(16px);

    // 如果设置了固定高度，使用移动端响应式高度
    &[style*='--input-height-mobile'] {
      height: var(--input-height-mobile);
    }
  }
}

// hover 状态
.x-input-field--hover {
  border-color: var(--x-purple-6);
  background-color: var(--x-back-2);
}

// focused 状态
.x-input-field--focused {
  border-color: var(--x-purple-6);
  background-color: var(--x-back-2);
}

// error 状态
.x-input-field--error {
  border-color: var(--x-error-6);
}

// disabled 状态
.x-input-field--disabled {
  background-color: var(--x-back-3);
  border-color: var(--x-back-5);
  cursor: not-allowed;
}

// 内部输入框
.x-input-field__inner {
  flex: 1;
  font-size: var(--x-text-14-size);
  color: var(--x-text-1);
  background: transparent;
  border: none;
  outline: none;
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}

.x-input-field__inner::placeholder {
  color: var(--x-text-7);
  opacity: 1;
}

.x-input-field__inner:disabled {
  cursor: not-allowed;
  color: var(--x-text-7);
  -webkit-text-fill-color: var(--x-text-7);
}

// error 状态下的内部输入框
.x-input-field--error .x-input-field__inner {
  color: var(--x-error-6);
}

// 后缀容器
.x-input-field__suffix {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 8px;

  @include mobile {
    gap: responsive-size(8px);
    margin-left: responsive-size(8px);
  }
}

// 清除按钮
.x-input-field__clear {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background-color: var(--x-back-1);
  cursor: pointer;
  border-radius: var(--x-radius-circle);
  transition: background-color 0.2s;

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(20px);
  }
}

.x-input-field__clear--hover {
  background-color: var(--x-back-7);
}

// 清除图标
.x-input-field__clear-icon {
  width: 20px;
  height: 20px;
  color: var(--x-back-1);

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(20px);
  }
}

.x-input-field__clear-icon:hover {
  color: var(--x-back-7);
}

// 密码切换按钮
.x-input-field__password-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  cursor: pointer;

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(20px);
  }
}

// 密码图标
.x-input-field__password-icon {
  width: 20px;
  height: 20px;
  color: var(--x-text-1);

  @include mobile {
    width: responsive-size(20px);
    height: responsive-size(20px);
  }
}

// error 状态下的密码图标
.x-input-field__password-toggle--error .x-input-field__password-icon {
  color: var(--x-error-6);
}
</style>
